// src/pages/publish/LessonPublicPage.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadCloud, RefreshCw, ArrowLeft } from 'lucide-react';
import { useRequest } from 'ahooks';

// API 引入
import {
  getInternalCourses,
  getPublishedCourses,
  // 发布
  publishCourse,
  publishStage,
  publishLesson,
  publishPlan, // 👈 新增
  publishSlide,
  // 删除线上
  deletePublishedCourse,
  deletePublishedStage,
  deletePublishedLesson,
  deletePublishedPlan, // 👈 新增
  deletePublishedSlide,
  type PublishCourse
} from '../../lib/api';

// Components
import { CourseTree } from './components/CourseTree';
import { EnvironmentColumn } from './components/EnvironmentColumn';
import LogoutButton from '../../components/common/LogoutButton';

const LessonPublicPage: React.FC = () => {
  const navigate = useNavigate();
  
  // 使用 ahooks 管理数据请求
  const { 
    data, 
    loading, 
    run: refreshData 
  } = useRequest(async () => {
    console.log('📡 Fetching publish data...');
    
    const [internalRes, publishedRes] = await Promise.all([
      getInternalCourses(),
      getPublishedCourses()
    ]);

    const internal = Array.isArray(internalRes) ? internalRes : (internalRes as any)?.data || [];
    const published = Array.isArray(publishedRes) ? publishedRes : (publishedRes as any)?.data || [];

    return { internal, published };
  }, {
    manual: false,
    debounceWait: 100,
    onError: (e) => {
      console.error("Data Load Error:", e);
      alert("加载数据失败，请检查网络");
    }
  });

  const internalCourses = data?.internal || [];
  const publishedCourses = data?.published || [];

  // src/pages/publish/LessonPublicPage.tsx 中的 handlePublish 函数

  const handlePublish = async (
    type: 'course' | 'stage' | 'lesson' | 'plan' | 'slide', 
    id: number, 
    extra?: any
  ) => {
    if (!confirm(`确认要发布此 ${type} 到线上吗？这将覆盖旧版本。`)) return;

    try {
      let res;
      switch (type) {
        case 'course': res = await publishCourse(id); break;
        case 'stage':  res = await publishStage(id); break;
        case 'lesson': res = await publishLesson(id); break;
        case 'plan':   res = await publishPlan(id); break;
        case 'slide':
          const defaultVer = extra?.defaultVersion || "";
          // 提示用户输入
          let versionInput = prompt("请输入发布版本号 (留空则使用最新):", defaultVer);
          
          if (versionInput === null) return; // 用户点击取消

          // 🔥 核心修复逻辑：清洗字符串
          let versionNum: number | undefined = undefined;
          
          if (versionInput.trim() !== "") {
            // 1. 使用正则替换掉非数字和小数点的字符 (兼容 v1.0, V2, vv3.5 等)
            // [^0-9.] 表示匹配所有"非数字且非小数点"的字符，将其替换为空
            const cleanVersion = versionInput.replace(/[^0-9.]/g, '');
            
            // 2. 尝试转为数字
            versionNum = Number(cleanVersion);

            // 3. 再次校验是否为有效数字 (防止用户输入 "abc" 变成空串后转为 0，或者 ".." 导致 NaN)
            if (isNaN(versionNum) || cleanVersion === "") {
              alert(`版本号格式错误！解析结果: "${cleanVersion}"，请输入纯数字 (如 1.0)`);
              return;
            }
          }

          // 构造 Payload (如果 versionNum 是 undefined，后端会发布最新版)
          const payload = versionNum !== undefined ? { version_number: versionNum } : undefined;
          
          console.log(`Publishing Slide ID: ${id}, Version: ${versionNum}`); // 调试日志
          res = await publishSlide(id, payload);
          break;
      }
      
      const msg = (res as any)?.data?.message || (res as any)?.message || '发布成功';
      console.log('Publish Success:', msg);
      alert(msg);
      refreshData(); 
    } catch (e: any) {
      console.error(e);
      alert(`发布失败: ${e.response?.data?.message || e.message}`);
    }
  };


  // --- 删除处理 (右侧触发 - 删除线上数据) ---
  const handleDeletePublished = async (
    type: 'course' | 'stage' | 'lesson' | 'plan' | 'slide', // 增加 plan
    id: number
  ) => {
    if (!confirm(`⚠️ 警告：确定要【下架/删除】线上的这个 ${type} 吗？此操作不可逆！`)) return;

    try {
      let res;
      switch (type) {
        case 'course': res = await deletePublishedCourse(id); break;
        case 'stage':  res = await deletePublishedStage(id); break;
        case 'lesson': res = await deletePublishedLesson(id); break;
        case 'plan':   
          // 🔥 新增 Plan 删除
          res = await deletePublishedPlan(id); 
          break;
        case 'slide':  res = await deletePublishedSlide(id); break;
      }
      
      const msg = (res as any)?.data?.message || (res as any)?.message || '删除成功';
      console.log('Delete Published Success:', msg);
      alert(msg);
      refreshData(); 
    } catch (e: any) {
      console.error(e);
      alert(`删除失败: ${e.response?.data?.message || e.message}`);
    }
  };

  // --- 渲染视图 ---
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[1600px] mx-auto h-full flex flex-col gap-6">
        
        {/* Header */}
        <header className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          {/* 左侧：返回按钮 */}
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-700 transition-all active:scale-95"
            >
              <ArrowLeft size={16} />
              返回管理中心
            </button>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <UploadCloud className="text-blue-600" />
              课程发布管理中心
            </h1>
          </div>
          {/* 右侧：刷新按钮和退出登录 */}
          <div className="flex items-center gap-4">
            <button 
              onClick={refreshData} 
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-700 transition-all active:scale-95 disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              {loading ? '加载中...' : '刷新数据'}
            </button>
            <LogoutButton />
          </div>
        </header>

        {/* Dual Pane Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(100vh-180px)]">
          
          {/* 左侧：内部环境 */}
          <EnvironmentColumn title="内部开发环境 (Internal)" type="internal">
            <CourseTree 
              courses={internalCourses} 
              isInternal={true} 
              onPublish={handlePublish}
              onDelete={() => {}} // 左侧暂不提供删除功能，根据需求可改为 deleteInternalXXX
            />
          </EnvironmentColumn>

          {/* 右侧：线上环境 */}
          <EnvironmentColumn title="已发布环境 (Public)" type="published">
            <CourseTree 
              courses={publishedCourses} 
              isInternal={false} 
              onPublish={() => {}} 
              onDelete={handleDeletePublished} 
            />
          </EnvironmentColumn>

        </div>
      </div>
    </div>
  );
};

export default LessonPublicPage;
